import {
  Menu,
  MenuHandler,
  MenuList,
  MenuItem,
  Card,
  CardBody,
  Typography,
  Button,
  Checkbox,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Select,
  Option,
} from "@material-tailwind/react";
import {
  ChevronDownIcon,
  IdentificationIcon,
  MapPinIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { useAppContextController, setSearchState } from "context";
import { capitalize } from "util/input";
import { useState } from "react";
import { getUserTokenForImpersonate } from "api/auth";
import { useCountUp } from "hooks/useCountUp";
import { useAccountSubscriptionHistory } from "hooks/useAccountSubscriptionHistory";
import { environment } from "configs/environment";
import {
  CSSearchAccount,
  CSSearchBusiness,
  CSSearchType,
  CSSearchUser,
} from "types";
import {
  displayBoolean,
  displayDate,
  displayInput,
  displayLink,
  getGoogleSearchUrl,
} from "util/text";
import {
  AccountSearchAccountDetails as AccountDetails,
  AccountSearchDetailsUL as DetailsUL,
  AccountSearchLocationDetails as LocationDetails,
  AccountSearchLocationsTable as LocationsTable,
  AccountSearchUsersTable as UsersTable,
} from "components/AccountSearch/AccountSearch.components";
import { CustomCardHeader as AccountsSearchHeader } from "components/Cards/CustomCardHeader";
import { CardLoadingState } from "components/Cards/CardLoadingState";
import { CardEmptyState } from "components/Cards/CardEmptyState";
import { CardBackground } from "components/Cards/CardBackground";
import { submitVerify } from "api/verify";
import { fetchSearchData } from "api/search";
import { updatePhoneConfigStatus } from "api/phoneConfig";
import { PhoneConfigTimeline } from "components/PhoneConfigTimeline/PhoneConfigTimeline";
import { SubscriptionPeriodTimeline } from "components/SubscriptionPeriodTimeline/SubscriptionPeriodTimeline";
import {
  periodGrossUsdCents,
  periodNetUsdCents,
} from "api/subscriptionPeriods";

const SuccessAccountState = () => {
  const [controller, dispatch] = useAppContextController();
  const data = controller.search.data as CSSearchAccount;
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const { account, users, locations } = data;
  const [isLoadingVerify, setIsLoadingVerify] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(
    account.phoneConfig?.status || "unverified",
  );
  const [originalStatus, setOriginalStatus] = useState(
    account.phoneConfig?.status || "unverified",
  );
  const [failedReason, setFailedReason] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [addnInfo, setAddnInfo] = useState("");
  const [legalBusinessName, setLegalBusinessName] = useState(
    account.brandRegistration?.legalBusinessName || "",
  );
  const [address2, setAddress2] = useState(
    account.brandRegistration?.address2 || "",
  );
  const [address1, setAddress1] = useState(
    account.brandRegistration?.address1 || "",
  );
  const [city, setCity] = useState(account.brandRegistration?.city || "");
  const [state, setState] = useState(account.brandRegistration?.state || "");
  const [country, setCountry] = useState(
    account.brandRegistration?.country || "",
  );
  const [postalCode, setPostalCode] = useState(
    account.brandRegistration?.postalCode || "",
  );
  const [firstName, setFirstName] = useState(
    account.brandRegistration?.firstName || "",
  );
  const [lastName, setLastName] = useState(
    account.brandRegistration?.lastName || "",
  );
  const [email, setEmail] = useState(account.brandRegistration?.email || "");
  const [websiteURL, setWebsiteURL] = useState(
    account.brandRegistration?.websiteURL || "",
  );
  const [phone, setPhone] = useState(account.brandRegistration?.phone || "");
  const [businessIdentifier, setBusinessIdentifier] = useState(
    account.brandRegistration?.businessIdentifier || "",
  );
  const [businessRegistrationType, setBusinessRegistrationType] = useState(
    account.brandRegistration?.businessRegistrationType || "",
  );
  const [businessIdentifierType, setBusinessIdentifierType] = useState(
    account.brandRegistration?.businessIdentifierType || "",
  );
  const [verifyError, setVerifyError] = useState("");
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [infoTab, setInfoTab] = useState<"details" | "locations" | "users">(
    "details",
  );

  // Lifetime value + subscription history share a single lazy fetch.
  const {
    periods: subscriptionPeriods,
    isLoading: subscriptionsLoading,
    error: subscriptionsError,
  } = useAccountSubscriptionHistory(account.id);

  const ltvGrossCents = subscriptionPeriods.reduce(
    (sum, p) => sum + periodGrossUsdCents(p),
    0,
  );
  const ltvNetCents = subscriptionPeriods.reduce(
    (sum, p) => sum + periodNetUsdCents(p),
    0,
  );

  const ltvGrossDollars = Math.round(ltvGrossCents / 100);
  const ltvNetDollars = Math.round(ltvNetCents / 100);
  const animatedGross = useCountUp(ltvGrossDollars, 600);
  const animatedNet = useCountUp(ltvNetDollars, 600);

  const ltvSubtitle =
    !subscriptionsLoading &&
    !subscriptionsError &&
    subscriptionPeriods.length > 0 ? (
      <span className="block text-lg font-semibold text-green-800">
        ${animatedGross.toLocaleString("en-US")}{" "}
        <span className="text-base font-medium text-blue-gray-500">
          (Net: ${animatedNet.toLocaleString("en-US")})
        </span>
      </span>
    ) : (
      "Account"
    );

  const infoChanged = ({ target }) => {
    setAddnInfo(target.value);
  };

  const legalNameChanged = ({ target }) => {
    setLegalBusinessName(target.value);
  };
  const address1Changed = ({ target }) => {
    setAddress1(target.value);
  };
  const cityChanged = ({ target }) => {
    setCity(target.value);
  };
  const phoneChanged = ({ target }) => {
    setPhone(target.value);
  };

  const emailChanged = ({ target }) => {
    setEmail(target.value);
  };

  const websiteChanged = ({ target }) => {
    setWebsiteURL(target.value);
  };

  const stateChanged = ({ target }) => {
    setState(target.value);
  };
  const countryChanged = ({ target }) => {
    setCountry(target.value);
  };
  const firstNameChanged = ({ target }) => {
    setFirstName(target.value);
  };
  const lastNameChanged = ({ target }) => {
    setLastName(target.value);
  };
  const postalCodeChanged = ({ target }) => {
    setPostalCode(target.value);
  };
  const address2Changed = ({ target }) => {
    setAddress2(target.value);
  };
  const businessIdentifierChanged = ({ target }) => {
    setBusinessIdentifier(target.value);
  };
  const businessRegistrationTypeChanged = ({ target }) => {
    setBusinessRegistrationType(target.value);
  };
  const businessIdentifierTypeChanged = ({ target }) => {
    setBusinessIdentifierType(target.value);
  };

  const handleSubmitVerification = async () => {
    try {
      setVerifyError("");
      setIsLoadingVerify(true);
      await submitVerify(
        account.id,
        addnInfo,
        address1,
        address2,
        city,
        state,
        postalCode,
        country,
        firstName,
        lastName,
        websiteURL,
        email,
        phone,
        legalBusinessName,
        businessIdentifier,
        businessIdentifierType,
        businessRegistrationType,
      );
      const data = await fetchSearchData(account.id);
      setSearchState(dispatch, { data, isLoading: false, error: undefined });
    } catch (error) {
      console.error("🚀 ~ handleSubmitVerify ~ error", error);
      setVerifyError((error as any).message || "Something went wrong");
    } finally {
      setIsLoadingVerify(false);
    }
  };

  const handleVerifyConfirmation = () => {
    setVerifyDialogOpen(false);
  };

  const submitVerification = async () => {
    setVerifyDialogOpen(false);
    await handleSubmitVerification();
  };

  const handleStatusChange = (value) => {
    setSelectedStatus(value);
    if (value !== originalStatus) {
      setStatusDialogOpen(true);
    }
  };

  const handleStatusConfirmation = async () => {
    try {
      setIsLoadingStatus(true);
      await updatePhoneConfigStatus(
        account.id,
        selectedStatus,
        originalStatus,
        failedReason,
        sendEmail,
      );
      setOriginalStatus(selectedStatus);
      setHistoryRefreshKey((k) => k + 1);
      const data = await fetchSearchData(account.id);
      setSearchState(dispatch, { data, isLoading: false, error: undefined });
    } catch (error) {
      console.error("Error updating status:", error);
      setSelectedStatus(originalStatus);
    } finally {
      setStatusDialogOpen(false);
      setIsLoadingStatus(false);
    }
  };

  const handleStatusCancel = () => {
    setSelectedStatus(originalStatus);
    setStatusDialogOpen(false);
  };

  return (
    <>
      <AccountsSearchHeader
        setTab={setInfoTab}
        tab={infoTab}
        tabs={[
          { value: "details", label: "Details", Icon: IdentificationIcon },
          { value: "locations", label: "Locations", Icon: MapPinIcon },
          { value: "users", label: "Users", Icon: UsersIcon },
        ]}
        title={account.name}
        subtitle={ltvSubtitle}
        headerSubContent={
          <>
            {(account.phoneConfig?.status === "submitted" ||
              account.phoneConfig?.status === "in-review" ||
              account.phoneConfig?.status === "failed") && (
              <>
                <Button
                  color="black"
                  size="sm"
                  className="mt-2"
                  onClick={() => setVerifyDialogOpen(true)}
                  loading={isLoadingVerify}
                >
                  {account.phoneConfig?.status === "failed" ||
                  account.phoneConfig?.status === "in-review"
                    ? "Re-submit Verification"
                    : "Submit Verification"}
                </Button>
                {verifyError && (
                  <Typography variant="small" color="red" className="mt-2">
                    {verifyError}
                  </Typography>
                )}
              </>
            )}
          </>
        }
      />
      {infoTab === "details" && (
        <div className="gird-cols-1 mb-12 grid gap-12 px-4 lg:grid-cols-2 xl:grid-cols-3">
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              Account Details
            </Typography>
            <AccountDetails account={account} />
          </div>
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              Business Registration
            </Typography>
            <DetailsUL
              details={{
                "Legal Name": displayLink({
                  label: account?.brandRegistration?.legalBusinessName,
                  url: getGoogleSearchUrl(
                    account.brandRegistration?.legalBusinessName || "",
                    account,
                  ),
                  internal: false,
                }),
                Address: [
                  account?.brandRegistration?.address1,
                  account?.brandRegistration?.address2 || "",
                  account?.brandRegistration?.city,
                  account?.brandRegistration?.state,
                  account?.brandRegistration?.postalCode,
                ]
                  .filter(Boolean)
                  .join(", "),
                "Contact Name": [
                  account?.brandRegistration?.firstName,
                  account?.brandRegistration?.lastName,
                ]
                  .filter(Boolean)
                  .join(" "),
                "Contact Phone": account?.brandRegistration?.phone,
                "Contact Email": account?.brandRegistration?.email,
                Website: displayLink({
                  label: account?.brandRegistration?.websiteURL,
                  url: account?.brandRegistration?.websiteURL,
                  internal: false,
                }),
                "Business Identification":
                  account?.brandRegistration?.businessIdentifier,
                "Business Registration Type":
                  account?.brandRegistration?.businessRegistrationType,
                "Business Identifier Type":
                  account?.brandRegistration?.businessIdentifierType,
              }}
            />
          </div>
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              Phone Config
            </Typography>
            <div className="mb-4">
              <Typography
                variant="small"
                color="blue-gray"
                className="mb-2 font-medium"
              >
                Verification Status
              </Typography>
              <Select
                value={selectedStatus}
                onChange={handleStatusChange}
                disabled={isLoadingStatus}
                className="w-full"
              >
                <Option value="unverified">Unverified</Option>
                <Option value="submitted">Submitted</Option>
                <Option value="pending">Pending</Option>
                <Option value="in-review">In-review</Option>
                <Option value="verified">Verified</Option>
                <Option value="failed">Failed</Option>
                <Option value="ignore">Ignore</Option>
              </Select>
            </div>
            <DetailsUL
              details={{
                "Phone Number": displayLink({
                  url: `https://dashboard.bandwidth.com/portal/r/a/5006907/numbers/details/${account?.phoneConfig?.phoneNumber}`,
                  label: `${account?.phoneConfig?.phoneNumber}`,
                  internal: false,
                }),
                "Phone type":
                  account.phoneConfig?.phoneType === "tollFree"
                    ? "Toll Free"
                    : account.phoneConfig?.phoneType,
                "Grace Period": displayDate(account?.phoneConfig?.gracePeriod),
                "Submitted by User": displayDate(
                  account?.brandRegistration?.submissionDate,
                ),
                "Submitted to Carriers": displayDate(
                  account?.phoneConfig?.pendingDate,
                ),
                "Verified Date": displayDate(
                  account?.phoneConfig?.verifiedDate,
                ),
                "Failure Reason": account.phoneConfig?.declineReasonDescription,
              }}
            />
            <div className="mt-6 border-t border-blue-gray-100 pt-4">
              <Typography variant="h6" color="blue-gray" className="mb-3">
                Status History
              </Typography>
              <PhoneConfigTimeline
                accountId={account.id}
                refreshKey={historyRefreshKey}
              />
            </div>
            <div className="mt-6 border-t border-blue-gray-100 pt-4">
              <Typography variant="h6" color="blue-gray" className="mb-3">
                Subscription History
              </Typography>
              <SubscriptionPeriodTimeline
                periods={subscriptionPeriods}
                isLoading={subscriptionsLoading}
                error={subscriptionsError}
              />
            </div>
          </div>
          <Dialog open={statusDialogOpen} handler={handleStatusCancel}>
            <DialogHeader>Change Verification Status?</DialogHeader>
            <DialogBody>
              <p className="mb-4">
                Are you sure you want to change the verification status from "
                {originalStatus}" to "{selectedStatus}"?
              </p>
              {selectedStatus === "failed" && (
                <>
                  <div className="mb-4">
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="mb-2 font-medium"
                    >
                      Failure Reason
                    </Typography>
                    <Select
                      value={failedReason}
                      onChange={(value) => setFailedReason(value as string)}
                      label="Select a reason"
                    >
                      <Option value="invalid_website">Invalid Website</Option>
                      <Option value="invalid_contact">
                        Contact Name Must Belong to a Business Representative
                      </Option>
                      <Option value="invalid_business_info">
                        Business information invalid. Name, Contact, Address or
                        Email
                      </Option>
                      {country === "CA" ? (
                        <Option value="invalid_CBN">
                          Invalid CBN or Miss-matched Business Name.
                        </Option>
                      ) : (
                        <Option value="invalid_EIN">
                          Invalid EIN or Miss-matched Business Name.
                        </Option>
                      )}
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="send-email"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    />
                    <label
                      htmlFor="send-email"
                      className="cursor-pointer text-sm text-blue-gray-700"
                    >
                      Send email notification
                    </label>
                  </div>
                </>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                variant="text"
                color="red"
                onClick={handleStatusCancel}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              <Button
                variant="gradient"
                color="green"
                onClick={handleStatusConfirmation}
                loading={isLoadingStatus}
              >
                <span>Confirm</span>
              </Button>
            </DialogFooter>
          </Dialog>

          <Dialog open={verifyDialogOpen} handler={handleVerifyConfirmation}>
            <DialogHeader>Submit Verification?</DialogHeader>
            <DialogBody className="h-[28rem] overflow-scroll">
              <DetailsUL
                details={{
                  "Legal Name": displayInput(
                    "legalName",
                    "Legal Name",
                    legalNameChanged,
                    legalBusinessName,
                  ),
                  Address1: displayInput(
                    "address2",
                    "Address 1",
                    address1Changed,
                    address1,
                  ),
                  Address2: displayInput(
                    "address2",
                    "Address 2",
                    address2Changed,
                    address2,
                  ),
                  City: displayInput("City", "City", cityChanged, city),
                  State: displayInput("state", "state", stateChanged, state),
                  Postal: displayInput(
                    "postalCode",
                    "Postal",
                    postalCodeChanged,
                    postalCode,
                  ),
                  Country: displayInput(
                    "country",
                    "country",
                    countryChanged,
                    country,
                  ),
                  "Contact First": displayInput(
                    "firstName",
                    "Contact First",
                    firstNameChanged,
                    firstName,
                  ),
                  "Contact Last": displayInput(
                    "lastName",
                    "Contact Last",
                    lastNameChanged,
                    lastName,
                  ),
                  "Contact Phone": displayInput(
                    "phone",
                    "Phone",
                    phoneChanged,
                    phone,
                  ),
                  "Contact Email": displayInput(
                    "email",
                    "Email",
                    emailChanged,
                    email,
                  ),
                  Website: displayInput(
                    "websiteURL",
                    "Website",
                    websiteChanged,
                    websiteURL,
                  ),
                  "Business Identifier": displayInput(
                    "businessIdentifier",
                    "Business Identifier (EIN/CBN)",
                    businessIdentifierChanged,
                    businessIdentifier,
                  ),
                  "Business Reg Type": displayInput(
                    "businessRegistrationType",
                    "(EIN/CBN)",
                    businessRegistrationTypeChanged,
                    businessRegistrationType,
                  ),
                  "Business Identifier Type": displayInput(
                    "businessIdentifierType",
                    "Business Identifier Type",
                    businessIdentifierTypeChanged,
                    businessIdentifierType,
                  ),
                  "Additional Info": displayInput(
                    "additionalInfo",
                    "Additional Info",
                    infoChanged,
                  ),
                }}
              ></DetailsUL>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="text"
                color="red"
                onClick={() => setVerifyDialogOpen(false)}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              <Button
                variant="gradient"
                color="green"
                onClick={submitVerification}
              >
                <span>Confirm</span>
              </Button>
            </DialogFooter>
          </Dialog>
        </div>
      )}
      {infoTab === "locations" && <LocationsTable locations={locations} />}
      {infoTab === "users" && (
        <UsersTable users={users} locations={locations} />
      )}
    </>
  );
};
const SuccessBusinessState = () => {
  const [controller] = useAppContextController();
  const data = controller.search.data as CSSearchBusiness;
  const { business, users } = data;
  const [infoTab, setInfoTab] = useState<"details" | "users">("details");
  return (
    <>
      <AccountsSearchHeader
        title={business.name}
        subtitle="Location"
        tab={infoTab}
        setTab={setInfoTab}
        tabs={[
          { value: "details", label: "Details", Icon: IdentificationIcon },
          { value: "users", label: "Users", Icon: UsersIcon },
        ]}
      />
      {infoTab === "details" && (
        <div className="gird-cols-1 mb-12 grid gap-12 px-4 lg:grid-cols-2 xl:grid-cols-3">
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              Location Details
            </Typography>
            <LocationDetails location={business} />
          </div>
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              Settings
            </Typography>
            <DetailsUL
              details={{
                "Self Check-in": displayBoolean(business.settings.selfCheckIn),
                Reservations: displayBoolean(
                  business.settings.enableReservations,
                ),
                "Online Reservations": displayBoolean(
                  business.settings.selfBooking,
                ),
                "Reserve with Google": displayBoolean(
                  business.settings.reserveWithGoogle,
                ),
              }}
            />
          </div>
        </div>
      )}
      {infoTab === "users" && (
        <UsersTable users={users} locations={[business]} />
      )}
    </>
  );
};

const SuccessUserState = () => {
  const [controller] = useAppContextController();
  const data = controller.search.data;
  const { user, account, locations } = data as CSSearchUser;
  const [infoTab, setInfoTab] = useState<"details" | "locations">("details");
  const [isLoadingImpersonate, setIsLoadingImpersonate] = useState(false);
  const [impersonateError, setImpersonateError] = useState("");

  const location = locations?.find((l) => l.id === user.businessId);

  const handleImpersonate = async (target: "waitly" | "waitly2" = "waitly") => {
    if (isLoadingImpersonate) return;
    const windowReference = window.open();

    try {
      setImpersonateError("");
      setIsLoadingImpersonate(true);
      if (!windowReference) {
        throw new Error("Please allow pop-ups to impersonate this user.");
      }
      windowReference.opener = null;
      const response = await getUserTokenForImpersonate(user.id);
      if (!response.token) {
        throw new Error("Unable to sign in as this user. Please try again.");
      }
      // Fragments keep the credential out of server access logs.
      const baseUrl =
        target === "waitly2" ? environment.nativeBaseUrl : environment.baseUrl;
      windowReference.location.href =
        baseUrl + "#authToken=" + encodeURIComponent(response.token);
    } catch (error) {
      windowReference?.close();
      setImpersonateError(
        error instanceof Error ? error.message : "Something went wrong",
      );
    } finally {
      setIsLoadingImpersonate(false);
    }
  };

  return (
    <>
      <AccountsSearchHeader
        title={`${user.firstName} ${user.lastName}`}
        subtitle={user.email}
        tab={infoTab}
        setTab={setInfoTab}
        tabs={[
          { value: "details", label: "Details", Icon: IdentificationIcon },
          { value: "locations", label: "Locations", Icon: MapPinIcon },
        ]}
        headerSubContent={
          <>
            <div className="mt-2 inline-flex">
              <Button
                color="black"
                size="sm"
                className="rounded-r-none"
                onClick={() => handleImpersonate()}
                loading={isLoadingImpersonate}
                disabled={isLoadingImpersonate}
                aria-label="Impersonate in Waitly"
                aria-busy={isLoadingImpersonate}
              >
                Impersonate
              </Button>
              <Menu placement="bottom-end">
                <MenuHandler>
                  <Button
                    color="black"
                    size="sm"
                    className="rounded-l-none border-l border-white/30 px-3"
                    disabled={isLoadingImpersonate}
                    aria-label="Choose app for impersonation"
                  >
                    <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </MenuHandler>
                <MenuList>
                  <MenuItem
                    disabled={isLoadingImpersonate}
                    onClick={() => handleImpersonate("waitly")}
                  >
                    Waitly
                  </MenuItem>
                  <MenuItem
                    disabled={isLoadingImpersonate}
                    onClick={() => handleImpersonate("waitly2")}
                  >
                    Waitly 2.0
                  </MenuItem>
                </MenuList>
              </Menu>
            </div>
            {impersonateError && (
              <Typography
                variant="small"
                color="red"
                className="mt-2"
                aria-live="polite"
              >
                {impersonateError}
              </Typography>
            )}
          </>
        }
      />
      {infoTab === "details" && (
        <div className="gird-cols-1 mb-12 grid gap-12 px-4 lg:grid-cols-2 xl:grid-cols-3">
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              User Details
            </Typography>
            <DetailsUL
              details={{
                UserId: user.id,
                Role: capitalize(user.role),
                Created: displayDate(user.authMetadata?.creationTime),
                "Signed In": displayDate(user.authMetadata?.lastSignInTime),
                Firebase: displayLink({
                  url: `${environment.firestoreUrl}~2Fusers~2F${user.id}`,
                  label: "Firebase",
                  internal: false,
                }),
              }}
            />
          </div>
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              Account Details
            </Typography>
            <AccountDetails account={account} />
          </div>
          <div>
            <Typography variant="h6" color="blue-gray" className="mb-3">
              Location Details
            </Typography>
            <LocationDetails location={location} />
          </div>
        </div>
      )}
      {infoTab === "locations" && <LocationsTable locations={locations} />}
    </>
  );
};

const ErrorState = ({ error }) => {
  return (
    <div className="flex min-h-96 items-center justify-center">
      <Typography variant="h5" color="red" className="my-4">
        {error}
      </Typography>
    </div>
  );
};

export function AccountsPage() {
  const [controller] = useAppContextController();

  const getState = () => {
    if (controller.search.isLoading) return "loading";
    if (controller.search.error) return "error";
    if (controller.search.data?.type === CSSearchType.User)
      return "success-user";
    if (controller.search.data?.type === CSSearchType.Account)
      return "success-account";
    if (controller.search.data?.type === CSSearchType.Business)
      return "success-business";
    return "empty";
  };
  const state = getState();

  return (
    <>
      <CardBackground />
      <Card className="mx-3 -mt-16 mb-6 border border-blue-gray-100 lg:mx-4">
        <CardBody className="p-4">
          {state === "loading" && <CardLoadingState />}
          {state === "empty" && (
            <CardEmptyState message="No Matching Details found" />
          )}
          {state === "error" && <ErrorState error={controller.search.error} />}
          {state === "success-user" && <SuccessUserState />}
          {state === "success-account" && <SuccessAccountState />}
          {state === "success-business" && <SuccessBusinessState />}
        </CardBody>
      </Card>
    </>
  );
}

export default AccountsPage;
